/**
 * RetailerLocations (/retaileradmin/locations)
 *
 * What the retailer is made of, for its own staff: the stores customers can
 * send estimates to, child locations (and whether they are in this member's
 * view), and the team with what each person sees.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Store, HardHat, Users, Loader2, Building2 } from 'lucide-react';
import { useRetailerContext } from './RetailerAdminContext';
import { fetchRetailerLocations, type RetailerLocationsResponse } from '../../services/retailerAdminApi';

export const RetailerLocations: React.FC = () => {
  const { t } = useTranslation('retaileradmin');
  const ctx = useRetailerContext();
  const [data, setData] = useState<RetailerLocationsResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setFailed(false);
    fetchRetailerLocations()
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [ctx.retailerId]);

  /** One line per person: their rows (one per node) merged. */
  const team = React.useMemo(() => {
    const out: { key: number; rows: NonNullable<typeof data>['team'] }[] = [];
    const idx = new Map<string, number>();
    for (const m of data?.team ?? []) {
      const k = m.email ?? String(m.id);
      const i = idx.get(k);
      if (i === undefined) { idx.set(k, out.length); out.push({ key: m.id, rows: [m] }); } else out[i].rows.push(m);
    }
    return out;
  }, [data]);

  const scopeText = (m: { scope: 'all' | 'locations' | 'none'; includeChildren: boolean; isChild: boolean; retailerName: string; locations: { id: number; name: string }[] }) => {
    if (m.scope === 'locations') return m.locations.map(l => l.name).join(', ');
    if (m.scope === 'none') return t('locations.scope.none');
    if (m.isChild) return t('locations.scope.allOfChild', { name: m.retailerName });
    return m.includeChildren ? t('locations.scope.allWithChildren') : t('locations.scope.all');
  };

  return (
    <div>
      <div className="bg-white rounded-3 shadow-sm p-4 mb-3">
        <h2 className="fw-bold mb-1 d-flex align-items-center gap-2"><MapPin size={22} /> {t('locations.title')}</h2>
        <p className="text-muted mb-0 small">{t('locations.subtitle', { retailer: ctx.retailerName })}</p>
      </div>

      {failed && <div className="alert alert-danger small">{t('locations.loadFailed')}</div>}
      {!data && !failed && <div className="text-center py-4"><Loader2 size={20} className="text-muted" style={{ animation: 'spin 1s linear infinite' }} /></div>}

      {data && (
        <div className="row g-3">
          <div className="col-12 col-xl-7">
            <div className="bg-white rounded-3 shadow-sm p-3 h-100">
              <h6 className="fw-bold mb-1 d-flex align-items-center gap-2"><Store size={16} /> {t('locations.stores')} <span className="badge text-bg-light border">{data.stores.length}</span></h6>
              <p className="text-muted small mb-2">{t('locations.storesHint')}</p>
              {data.stores.length === 0 ? (
                <p className="text-muted small mb-0">{t('locations.storesEmpty')}</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead>
                      <tr className="text-muted small">
                        <th>{t('locations.columns.store')}</th>
                        <th className="d-none d-sm-table-cell">{t('locations.columns.city')}</th>
                        <th className="d-none d-md-table-cell">{t('locations.columns.type')}</th>
                        {data.children.some(c => c.included) && <th className="d-none d-md-table-cell">{t('locations.columns.linkedTo')}</th>}
                        <th className="text-center">{t('locations.columns.members')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.stores.map(s => (
                        <tr key={s.id}>
                          <td className="small fw-semibold">{s.name}{s.mine && <span className="badge text-bg-light border ms-1">{t('locations.mine')}</span>}</td>
                          <td className="small d-none d-sm-table-cell">{[s.city, s.region].filter(Boolean).join(', ') || '—'}</td>
                          <td className="small d-none d-md-table-cell">
                            <span className="d-inline-flex align-items-center gap-1 text-muted">
                              {s.kind === 'installer' ? <HardHat size={13} /> : <Store size={13} />} {t(`locations.kinds.${s.kind}`)}
                            </span>
                          </td>
                          {data.children.some(c => c.included) && <td className="small d-none d-md-table-cell">{s.isChild ? s.retailerName : ctx.retailerName}</td>}
                          <td className="text-center small">{s.memberCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="col-12 col-xl-5">
            <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
              <h6 className="fw-bold mb-2 d-flex align-items-center gap-2"><Building2 size={16} /> {t('locations.children')} <span className="badge text-bg-light border">{data.children.length}</span></h6>
              {data.children.length === 0 ? (
                <p className="text-muted small mb-0">{t('locations.childrenEmpty')}</p>
              ) : (
                <ul className="list-unstyled small mb-0">
                  {data.children.map(c => (
                    <li key={c.id} className="d-flex align-items-center justify-content-between py-1 border-bottom">
                      <span>{c.name}</span>
                      <span className={`badge ${c.included ? 'text-bg-success' : 'text-bg-secondary'}`}>{c.included ? t('locations.childIncluded') : t('locations.childExcluded')}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white rounded-3 shadow-sm p-3">
              <h6 className="fw-bold mb-1 d-flex align-items-center gap-2"><Users size={16} /> {t('locations.team')} <span className="badge text-bg-light border">{team.length}</span></h6>
              <p className="text-muted small mb-2">{t('locations.teamHint')}</p>
              {data.team.length === 0 ? (
                <p className="text-muted small mb-0">{t('locations.teamEmpty')}</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr className="text-muted small">
                        <th>{t('locations.columns.person')}</th>
                        <th>{t('locations.columns.role')}</th>
                        <th>{t('locations.columns.sees')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {team.map(g => {
                        const m = g.rows[0];
                        const role = g.rows.some(r => r.role === 'manager') ? 'manager' : 'viewer';
                        return (
                          <tr key={g.key}>
                            <td className="small">
                              <div className="fw-semibold">{[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}{m.isSelf && <span className="badge bg-light text-dark border ms-1">{t('locations.you')}</span>}</div>
                              <div className="text-muted" style={{ fontSize: '0.8em' }}>{m.email}</div>
                            </td>
                            <td className="small"><span className={`badge ${role === 'manager' ? 'text-bg-primary' : 'text-bg-light border'}`}>{t(`roles.${role}`)}</span></td>
                            <td className="small">{g.rows.map(r => <div key={r.id}>{scopeText(r)}</div>)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetailerLocations;
